import React from 'react';
import {
  Action, ActionProps, FormHandlers, Portal, useModal, useNotifications,
} from '@doveit/bricks';
import { HRRecall, JobApplication } from '../../../providers/api/dtos';
import SimpleModal from '../../../components/simple-modal/SimpleModal';
import HRRecallForm, { HRRecallFormModel } from '../../components/hr-recall-form/HRRecallForm';
import useRBAC from '../../../hooks/use-rbac/useRBAC';
import { hrRecallFormModelToHRRecall, hrRecallToHRRecallFormModel } from '../../components/hr-recall-form/mappers';
import { createHRCalendarEvent, updateHRCalendarEvent } from '../../../providers/api/hr/hrProvider';
import { updateJobApplication } from '../../../providers/api/job-application/jobApplicationProvider';

export const HR_RECALL_CREATE_SUCCESS_MESSAGE = 'La recall è stata fissata con successo.';
export const HR_RECALL_CREATE_ERROR_MESSAGE = 'Non è stato possibile fissare la recall.';
export const HR_RECALL_UPDATE_SUCCESS_MESSAGE = 'La recall è stata aggiornata con successo.';
export const HR_RECALL_UPDATE_ERROR_MESSAGE = 'Non è stato possibile aggiornare la recall.';

interface ChildFnProps {
  openModal: VoidFunction,
  isSaving: boolean,
}

interface BaseProps extends React.AriaAttributes {
  jobApplication: JobApplication,
  onSuccess?: (recall: HRRecall) => void,
  children?: (childFnProps: ChildFnProps) => React.ReactNode,
  size?: ActionProps['size'],
}

export interface CreateHRRecallActionProps extends BaseProps {
  hrRecall?: HRRecall & { id?: undefined },
  canCreate: boolean,
  canUpdate?: never,
}

export interface UpdateHRRecallActionProps extends BaseProps {
  hrRecall: HRRecall,
  canUpdate: boolean,
  canCreate?: never,
}

export type UpsertHRRecallActionProps = CreateHRRecallActionProps | UpdateHRRecallActionProps;

const UpsertHRRecallAction: React.FC<UpsertHRRecallActionProps> = ({
  jobApplication,
  hrRecall,
  canCreate,
  canUpdate,
  children,
  onSuccess,
  size = 'S',
  ...rest
}) => {
  const formRef = React.useRef<FormHandlers>(null) as React.MutableRefObject<FormHandlers>;
  const [isSaving, setIsSaving] = React.useState(false);

  const { user, userIsHR } = useRBAC();
  const modal = useModal();
  const { addSuccess, addError } = useNotifications();
  const isCreation = !hrRecall?.id;

  const canDoAction = isCreation ? (canCreate ?? false) : (canUpdate ?? false);

  const handleSubmit = React.useCallback(async (values: HRRecallFormModel) => {
    setIsSaving(true);

    try {
      const payload = hrRecallFormModelToHRRecall(values, jobApplication, hrRecall);

      let upsertedRecall: HRRecall;

      if (!jobApplication.supervisorEmail) {
        const jobApplicationToUpdate: JobApplication = {
          ...jobApplication,
          supervisorEmail: values.supervisorEmail,
        };

        await updateJobApplication(jobApplicationToUpdate.id!, jobApplicationToUpdate);
      }

      if (isCreation) {
        upsertedRecall = await createHRCalendarEvent(payload);
      } else {
        upsertedRecall = await updateHRCalendarEvent(hrRecall.id!, payload);
      }

      setIsSaving(false);
      addSuccess(isCreation ? HR_RECALL_CREATE_SUCCESS_MESSAGE : HR_RECALL_UPDATE_SUCCESS_MESSAGE);
      modal.close();

      onSuccess?.(upsertedRecall);
    } catch {
      setIsSaving(false);
      addError(isCreation ? HR_RECALL_CREATE_ERROR_MESSAGE : HR_RECALL_UPDATE_ERROR_MESSAGE);
    }
  }, [addError, addSuccess, hrRecall, isCreation, jobApplication, modal, onSuccess]);

  if (!canDoAction) return null;

  return (
    <>
      {children
        ? children({
          isSaving,
          openModal: modal.open,
        }) : (
          <Action
            aria-label={`Azione per ${isCreation ? 'fissare' : 'modificare'} una recall`}
            label={`${isCreation ? 'Fissa' : 'Modifica'} recall`}
            onClick={() => modal.open()}
            loading={isSaving}
            size={size}
            {...rest}
          />
        )}
      <Portal>
        <SimpleModal
          {...modal}
          title={`${isCreation ? 'Fissa' : 'Modifica'} recall`}
          aria-label={`Modale per ${isCreation ? 'fissare' : 'modificare'} una recall`}
          footer={(
            <Action
              label={isCreation ? 'Fissa' : 'Modifica'}
              color="primary"
              emphasis="high"
              aria-label={`${isCreation ? 'Fissa' : 'Modifica'} recall`}
              onClick={() => formRef.current.handleSubmit()}
              loading={isSaving}
            />
          )}
        >
          <HRRecallForm
            initialValues={hrRecall
              ? hrRecallToHRRecallFormModel(
                hrRecall,
                jobApplication,
                { supervisorEmail: userIsHR ? user.email : '' },
              )
              : {
                supervisorEmail: jobApplication.supervisorEmail || (userIsHR ? user.email : ''),
              }}
            hiddenFields={{
              supervisorEmail: !!jobApplication.supervisorEmail,
            }}
            innerRef={formRef}
            onSubmit={handleSubmit}
            loading={isSaving}
          />
        </SimpleModal>
      </Portal>
    </>
  );
};

export default UpsertHRRecallAction;
